import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FAQ as FAQ_TID } from "@/constants/testIds";
import { Plus, Minus } from "lucide-react";

const ITEMS = [
  {
    q: "Do I need special hardware to use Mymech?",
    a: "No. You can start with your phone camera or a photo of the dashboard. For live telemetry, any $25 OBD-II Bluetooth adapter works. Mymech is hardware-agnostic.",
  },
  {
    q: "How accurate is the AI compared to a real mechanic?",
    a: "In a 12,000-repair benchmark against three ASE-certified shops, Mymech matched or beat the shop diagnosis 94% of the time on OBD-II-detectable faults. Non-code issues (rattles, smells) still benefit from a human eye.",
  },
  {
    q: "Is my vehicle data private?",
    a: "Your VIN, mileage, and telemetry never leave our encrypted pipeline. We don't sell to insurers, dealers, or advertisers. Ever. You can delete every trace with one click.",
  },
  {
    q: "Which cars are supported?",
    a: "62,400+ trims from 1996 onward across 40+ manufacturers, from a base Civic to a Porsche 992. EVs (Tesla, Rivian, Lucid, Ioniq) are first-class citizens.",
  },
  {
    q: "Can I share the repair plan with my mechanic?",
    a: "Yes. Every plan exports as a mechanic-grade PDF with OEM part numbers, torque specs, labor hours, and a diagnostic trace. Shops love it.",
  },
  {
    q: "What if the AI is wrong?",
    a: "Every recommendation is confidence-scored and shows its reasoning trace. If our plan misses, Mymech pays for the diagnostic fee at any partner shop, up to $150.",
  },
];

const Row = ({ item, i, isOpen, onToggle }) => {
  return (
    <div
      data-testid={FAQ_TID.item(i)}
      className="border-b border-white/10"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between text-left py-6 lg:py-8 group"
      >
        <span className="font-display text-xl lg:text-2xl font-medium text-white tracking-tight pr-8 group-hover:text-mclaren transition-colors">
          {item.q}
        </span>
        <span className="shrink-0 h-8 w-8 border border-white/20 flex items-center justify-center group-hover:border-mclaren group-hover:text-mclaren transition-colors">
          {isOpen ? <Minus className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        </span>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pb-8 text-white/60 text-base font-light leading-relaxed max-w-3xl">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      id="faq"
      data-testid={FAQ_TID.section}
      className="relative bg-obsidian py-24 lg:py-40 border-t border-white/5"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <span className="chip-brand">/ 07 · faq</span>
          <h2 className="mt-6 font-display text-4xl sm:text-5xl font-semibold text-white tracking-tighter leading-[0.95] sticky top-24">
            Questions we get<br />in the pits.
          </h2>
        </div>
        <div className="lg:col-span-8">
          {ITEMS.map((it, i) => (
            <Row
              key={it.q}
              item={it}
              i={i}
              isOpen={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
